export type SettingKind = 'toggle' | 'select' | 'action';

export interface DashboardSettingItem {
  id: string;
  label: string;
  hint?: string;
  kind: SettingKind;
  /** For select: value/label pairs */
  options?: { value: string; label: string }[];
  /** Shown disabled with “Coming soon” */
  future?: boolean;
  /** In-app link for action rows */
  href?: string;
  anchor?: string;
}

export interface DashboardSettingCategory {
  id: string;
  title: string;
  description: string;
  items: DashboardSettingItem[];
}

export const DASHBOARD_SETTING_CATEGORIES: DashboardSettingCategory[] = [
  {
    id: 'appearance',
    title: 'Appearance & UI',
    description: 'How District88 looks and feels on your device.',
    items: [
      {
        id: 'appearance_accent',
        label: 'Accent color',
        hint: 'Brand highlight for buttons and links.',
        kind: 'select',
        options: [
          { value: 'red', label: 'District red' },
          { value: 'rose', label: 'Rose' },
          { value: 'amber', label: 'Amber' },
          { value: 'emerald', label: 'Emerald' },
        ],
      },
      {
        id: 'appearance_font_size',
        label: 'Font size',
        kind: 'select',
        options: [
          { value: 'sm', label: 'Small' },
          { value: 'md', label: 'Medium' },
          { value: 'lg', label: 'Large' },
        ],
      },
      {
        id: 'appearance_density',
        label: 'Layout density',
        kind: 'select',
        options: [
          { value: 'compact', label: 'Compact' },
          { value: 'comfortable', label: 'Comfortable' },
        ],
      },
      {
        id: 'appearance_product_view',
        label: 'Product browsing',
        hint: 'Grid or list on market-style pages.',
        kind: 'select',
        options: [
          { value: 'grid', label: 'Grid' },
          { value: 'list', label: 'List' },
        ],
      },
      {
        id: 'appearance_reduce_motion',
        label: 'Reduce motion',
        hint: 'Limits animations for accessibility.',
        kind: 'toggle',
      },
      {
        id: 'appearance_high_contrast',
        label: 'High contrast',
        kind: 'toggle',
      },
      {
        id: 'appearance_language',
        label: 'Language',
        kind: 'select',
        options: [
          { value: 'en', label: 'English' },
          { value: 'es', label: 'Español (beta)' },
        ],
      },
      {
        id: 'appearance_region',
        label: 'Region display',
        kind: 'select',
        options: [
          { value: 'us', label: 'United States' },
          { value: 'intl', label: 'International' },
        ],
      },
      {
        id: 'appearance_currency',
        label: 'Currency display',
        kind: 'select',
        options: [
          { value: 'USD', label: 'USD ($)' },
          { value: 'EUR', label: 'EUR (€)' },
        ],
      },
    ],
  },
  {
    id: 'notifications',
    title: 'Notifications',
    description: 'Email, in-app, and (later) push / SMS.',
    items: [
      { id: 'notif_order_updates', label: 'Order updates', kind: 'toggle' },
      { id: 'notif_shipping_updates', label: 'Shipping updates', kind: 'toggle' },
      { id: 'notif_payouts', label: 'Payout notifications', kind: 'toggle' },
      { id: 'notif_messages', label: 'Messages from buyers / sellers', kind: 'toggle' },
      { id: 'notif_price_drops', label: 'Price drop alerts', kind: 'toggle' },
      { id: 'notif_restock', label: 'Restock alerts', kind: 'toggle' },
      { id: 'notif_new_releases', label: 'New releases', kind: 'toggle' },
      { id: 'notif_promotions', label: 'Promotions & marketing emails', kind: 'toggle' },
      { id: 'notif_sms', label: 'SMS notifications', hint: 'Mobile carrier — coming later.', kind: 'toggle', future: true },
      { id: 'notif_push', label: 'Push notifications', hint: 'When the native app ships.', kind: 'toggle', future: true },
      { id: 'notif_weekly_summary', label: 'Weekly summary emails', kind: 'toggle' },
      { id: 'notif_security', label: 'Security alerts', kind: 'toggle' },
    ],
  },
  {
    id: 'account',
    title: 'Account & profile',
    description: 'Identity and what others see.',
    items: [
      { id: 'acct_username', label: 'Username', hint: 'Change on your profile.', kind: 'action', href: '/profile' },
      { id: 'acct_avatar', label: 'Profile photo', kind: 'action', href: '/profile' },
      { id: 'acct_bio', label: 'Bio', kind: 'action', href: '/profile' },
      { id: 'acct_social_links', label: 'Social links (IG, TikTok, …)', kind: 'action', href: '/profile' },
      {
        id: 'acct_account_type',
        label: 'Primary mode',
        hint: 'How you mainly use District88.',
        kind: 'select',
        options: [
          { value: 'buyer', label: 'Buyer' },
          { value: 'seller', label: 'Seller' },
          { value: 'both', label: 'Both' },
        ],
      },
      { id: 'acct_verified_badge', label: 'Show verified badge', hint: 'When you are verified.', kind: 'toggle' },
      { id: 'acct_public_profile', label: 'Public profile', hint: 'When off, only basic info is visible.', kind: 'toggle' },
      { id: 'acct_show_sold', label: 'Show sold items on profile', kind: 'toggle' },
      { id: 'acct_show_location', label: 'Display city / region on profile', kind: 'toggle' },
      { id: 'acct_activity_visibility', label: 'Show recent activity on profile', kind: 'toggle' },
    ],
  },
  {
    id: 'payments',
    title: 'Payments & payouts',
    description: 'Cards, Stripe Connect, and seller payouts.',
    items: [
      { id: 'pay_methods', label: 'Manage payment methods', kind: 'action', href: '/checkout' },
      { id: 'pay_default_method', label: 'Remember default payment method', kind: 'toggle' },
      { id: 'pay_payout_method', label: 'Payout via Stripe Connect', hint: 'Managed in seller dashboard.', kind: 'action', href: '/sell/dashboard' },
      {
        id: 'pay_payout_schedule',
        label: 'Payout cadence preference',
        kind: 'select',
        options: [
          { value: 'standard', label: 'Standard (Stripe)' },
          { value: 'daily', label: 'Daily — when available' },
          { value: 'weekly', label: 'Weekly — when available' },
        ],
      },
      { id: 'pay_earnings_dashboard', label: 'Earnings summary emails', kind: 'toggle' },
      { id: 'pay_auto_withdraw', label: 'Auto-withdraw when balance clears', kind: 'toggle', future: true },
      { id: 'pay_tax_reminders', label: 'Tax document reminders (W-9 / W-8)', kind: 'toggle' },
      { id: 'pay_fx_display', label: 'Show converted amounts in browse', kind: 'toggle', future: true },
      { id: 'pay_tx_history_export', label: 'Transaction history export reminders', kind: 'toggle' },
      { id: 'pay_refund_prefs', label: 'Refund preference: favor store credit', kind: 'toggle', future: true },
      { id: 'pay_fee_breakdown', label: 'Show fee breakdown before checkout', kind: 'toggle' },
      { id: 'pay_min_payout', label: 'Notify when balance hits payout minimum', kind: 'toggle' },
    ],
  },
  {
    id: 'selling',
    title: 'Selling settings',
    description: 'Defaults for new listings and your storefront.',
    items: [
      {
        id: 'sell_handling_time',
        label: 'Handling time',
        kind: 'select',
        options: [
          { value: '1', label: '1 business day' },
          { value: '2', label: '2 business days' },
          { value: '3', label: '3 business days' },
        ],
      },
      { id: 'sell_auto_accept_offers', label: 'Auto-accept offers at list price', kind: 'toggle', future: true },
      { id: 'sell_auto_decline_low', label: 'Auto-decline offers below threshold %', kind: 'toggle', future: true },
      { id: 'sell_inventory_tracking', label: 'Inventory tracking on variants', kind: 'toggle', future: true },
      { id: 'sell_auto_relist', label: 'Auto-relist when quantity returns', kind: 'toggle', future: true },
      { id: 'sell_listing_visibility', label: 'New listings start as public', kind: 'toggle' },
      { id: 'sell_vacation_mode', label: 'Vacation mode (pause store)', kind: 'toggle', future: true },
      { id: 'sell_returns_policy', label: 'Show return policy on listings', kind: 'toggle' },
      { id: 'sell_accept_offers', label: 'Accept offers on listings by default', kind: 'toggle' },
      { id: 'sell_price_autopilot', label: 'AI-assisted price suggestions', kind: 'toggle', future: true },
      { id: 'sell_featured_prefs', label: 'Eligible for featured placement', kind: 'toggle', future: true },
      { id: 'sell_tier_insights', label: 'Seller tier insights in dashboard', kind: 'toggle', future: true },
    ],
  },
  {
    id: 'security',
    title: 'Security & privacy',
    description: 'Access, data, and account safety.',
    items: [
      { id: 'sec_change_password', label: 'Change password', kind: 'action', anchor: 'change-password' },
      { id: 'sec_2fa', label: 'Two-factor authentication (2FA)', kind: 'toggle', future: true },
      { id: 'sec_login_alerts', label: 'Login alerts by email', kind: 'toggle' },
      { id: 'sec_sessions', label: 'Active sessions reminder', hint: 'Sign out elsewhere from one email.', kind: 'toggle', future: true },
      { id: 'sec_data_export', label: 'Data download (export request)', kind: 'toggle', future: true },
      { id: 'sec_account_delete', label: 'Account deletion', kind: 'action', anchor: 'delete-account' },
      { id: 'sec_block_users', label: 'Allow blocking from messages', kind: 'toggle', future: true },
      { id: 'sec_reporting', label: 'Safety reporting shortcuts in feed', kind: 'toggle' },
      { id: 'sec_identity_status', label: 'Identity verification reminders', kind: 'action', href: '/identity-verification' },
      { id: 'sec_hide_personal', label: 'Hide email on receipts where possible', kind: 'toggle', future: true },
    ],
  },
  {
    id: 'social',
    title: 'Social & community',
    description: 'Feed, DMs, and discovery.',
    items: [
      { id: 'soc_follow_suggestions', label: 'Suggested follows in feed', kind: 'toggle' },
      { id: 'soc_feed_prefs', label: 'Personalized feed ordering', kind: 'toggle' },
      { id: 'soc_comment_notif', label: 'Comment notifications', kind: 'toggle' },
      { id: 'soc_like_notif', label: 'Like notifications', kind: 'toggle' },
      {
        id: 'soc_dm_who',
        label: 'Who can message you',
        kind: 'select',
        options: [
          { value: 'everyone', label: 'Everyone' },
          { value: 'following', label: 'People you follow' },
          { value: 'off', label: 'DMs off' },
        ],
      },
      {
        id: 'soc_content_visibility',
        label: 'Default post visibility',
        kind: 'select',
        options: [
          { value: 'public', label: 'Public' },
          { value: 'followers', label: 'Followers only' },
        ],
      },
      { id: 'soc_creator_mode', label: 'Creator mode (extra profile tools)', kind: 'toggle', future: true },
      { id: 'soc_auto_share', label: 'Cross-post new listings to feed', kind: 'toggle', future: true },
      { id: 'soc_trending_topics', label: 'Trending topics in sidebar', kind: 'toggle', future: true },
      {
        id: 'soc_mute_keywords',
        label: 'Mute keywords in feed',
        hint: 'Add phrases in comma-separated form under key soc_mute_terms_csv (synced in dashboard prefs).',
        kind: 'toggle',
        future: true,
      },
    ],
  },
  {
    id: 'smart',
    title: 'Smart & advanced',
    description: 'Analytics, automation, and power-user tools.',
    items: [
      { id: 'smart_ai_price', label: 'AI price suggestions when listing', kind: 'toggle', future: true },
      { id: 'smart_auto_undercut', label: 'Auto-adjust vs competitors', kind: 'toggle', future: true },
      { id: 'smart_sales_analytics', label: 'Weekly sales analytics email', kind: 'toggle', future: true },
      { id: 'smart_conversion', label: 'Conversion tips in seller dashboard', kind: 'toggle', future: true },
      { id: 'smart_saved_searches', label: 'Saved searches & alerts', kind: 'toggle', future: true },
      { id: 'smart_watchlist_digest', label: 'Watchlist digest emails', kind: 'toggle' },
      { id: 'smart_recently_viewed', label: 'Show recently viewed on home', kind: 'toggle' },
      { id: 'smart_recommendations', label: 'Personalized recommendations', kind: 'toggle' },
      { id: 'smart_ab_testing', label: 'Opt in to listing experiments (A/B)', kind: 'toggle', future: true },
      { id: 'smart_storefront_layout', label: 'Custom storefront layout', kind: 'toggle', future: true },
      { id: 'smart_api_access', label: 'API / developer access', kind: 'toggle', future: true },
    ],
  },
];
